import React, { useEffect, useState } from "react";
import axios from "axios";
import DashboardLayout from "./DashboardLayout";

const API = "/backend/public/api";

const heroStyle = {
  background: "linear-gradient(135deg, #4e1b24 0%, #7c2d3a 48%, #c2410c 100%)",
  borderRadius: "28px",
  padding: "28px",
  color: "#ffffff",
  boxShadow: "0 28px 60px rgba(124, 45, 58, 0.20)",
};

const panelStyle = {
  background: "#ffffff",
  borderRadius: "22px",
  padding: "22px",
  border: "1px solid rgba(148, 163, 184, 0.16)",
  boxShadow: "0 18px 38px rgba(15, 23, 42, 0.08)",
};

function SessionControl() {
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState("All Sessions");
  const [newSession, setNewSession] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  const broadcastSessionChange = (nextSession) => {
    localStorage.setItem("activeAcademicSession", nextSession || "All Sessions");
    window.dispatchEvent(new Event("academic-session-changed"));
  };

  const loadSessions = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/academic-sessions`);
      setSessions(res.data?.sessions || []);
      const nextSession = res.data?.active_session || "All Sessions";
      setActiveSession(nextSession);
      broadcastSessionChange(nextSession);
    } catch (error) {
      setSessions([]);
      setMessage("Session list load failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSessions();
  }, []);

  const addSession = async () => {
    if (!newSession.trim()) return;

    try {
      setSaving(true);
      const res = await axios.post(`${API}/academic-sessions`, {
        session_name: newSession,
      });
      setMessage(res.data?.message || "Session created successfully");
      setNewSession("");
      broadcastSessionChange(res.data?.active_session || activeSession);
      await loadSessions();
    } catch (error) {
      setMessage(error.response?.data?.message || "Session create failed");
    } finally {
      setSaving(false);
    }
  };

  const activateSession = async (id) => {
    try {
      setSaving(true);
      const res = await axios.post(`${API}/academic-sessions/${id}/activate`);
      setMessage(res.data?.message || "Active session updated");
      broadcastSessionChange(res.data?.active_session || "All Sessions");
      await loadSessions();
    } catch (error) {
      setMessage(error.response?.data?.message || "Session activation failed");
    } finally {
      setSaving(false);
    }
  };

  const deleteSession = async (id) => {
    try {
      setSaving(true);
      const res = await axios.delete(`${API}/academic-sessions/${id}`);
      setMessage(res.data?.message || "Session deleted successfully");
      broadcastSessionChange(res.data?.active_session || "All Sessions");
      await loadSessions();
    } catch (error) {
      setMessage(error.response?.data?.message || "Session delete failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <div style={{ display: "grid", gap: "20px" }}>
        <section style={heroStyle}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: "18px", flexWrap: "wrap", alignItems: "flex-start" }}>
            <div style={{ maxWidth: "760px" }}>
              <p style={{ margin: 0, fontSize: "12px", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", opacity: 0.82 }}>
                Session Control
              </p>
              <h1 style={{ margin: "10px 0 12px", fontSize: "34px", lineHeight: 1.1 }}>
                Master academic session that drives admission and pass-out records
              </h1>
              <p style={{ margin: 0, color: "rgba(255,255,255,0.84)", lineHeight: 1.75 }}>
                The active session selected here will flow into student admission, certificate generation, marksheet output, ID card references, and future verification records.
              </p>
            </div>

            <div style={{ ...panelStyle, width: "280px", maxWidth: "100%", color: "#0f172a" }}>
              <div style={{ color: "#64748b", fontSize: "12px", fontWeight: 700 }}>Current Active Session</div>
              <div style={{ marginTop: "8px", fontSize: "24px", fontWeight: 800, color: "#7c2d3a" }}>{activeSession}</div>
              <div style={{ marginTop: "10px", fontSize: "12px", color: "#64748b", lineHeight: 1.6 }}>
                Once changed, the rest of the software will follow this academic session.
              </div>
            </div>
          </div>
        </section>

        <section style={{ ...panelStyle, display: "grid", gap: "16px" }}>
          <div>
            <h3 style={{ margin: 0, color: "#0f172a" }}>Create New Session</h3>
            <p style={{ margin: "6px 0 0", color: "#64748b" }}>Add a new session that can be activated from here or from the dashboard hero selector.</p>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "minmax(220px, 1fr) auto", gap: "12px" }}>
            <input
              className="form-control"
              value={newSession}
              onChange={(event) => setNewSession(event.target.value)}
              placeholder="Example: 2024-2025"
            />
            <button type="button" className="btn btn-primary" onClick={addSession} disabled={saving}>
              {saving ? "Saving..." : "Add Session"}
            </button>
          </div>

          {message ? <div style={{ color: message.toLowerCase().includes("failed") ? "#b91c1c" : "#166534", fontWeight: 700 }}>{message}</div> : null}
        </section>

        <section style={panelStyle}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", flexWrap: "wrap", alignItems: "center", marginBottom: "18px" }}>
            <div>
              <h3 style={{ margin: 0, color: "#0f172a" }}>Session List</h3>
              <p style={{ margin: "6px 0 0", color: "#64748b" }}>Keep one session active so all upcoming records use the same academic cycle.</p>
            </div>
            <button type="button" className="btn btn-outline-dark" onClick={loadSessions} disabled={loading}>
              {loading ? "Loading..." : "Refresh"}
            </button>
          </div>

          <div className="table-responsive">
            <table className="table align-middle mb-0">
              <thead>
                <tr>
                  <th>Sl No</th>
                  <th>Session</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {!loading && sessions.map((session, index) => (
                  <tr key={session.id}>
                    <td>{index + 1}</td>
                    <td><strong>{session.session_name}</strong></td>
                    <td>
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          padding: "6px 10px",
                          borderRadius: "999px",
                          fontSize: "12px",
                          fontWeight: 700,
                          background: session.is_active ? "#dcfce7" : "#e2e8f0",
                          color: session.is_active ? "#166534" : "#475569",
                        }}
                      >
                        {session.is_active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => activateSession(session.id)}
                          disabled={saving || session.is_active}
                        >
                          Make Active
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => deleteSession(session.id)}
                          disabled={saving}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!loading && sessions.length === 0 && (
                  <tr>
                    <td colSpan="4" className="text-center py-4 text-muted">No session created yet.</td>
                  </tr>
                )}
                {loading && (
                  <tr>
                    <td colSpan="4" className="text-center py-4 text-muted">Loading session records...</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </DashboardLayout>
  );
}

export default SessionControl;



